/**
 * Portfolio dashboard: KPIs for the selected period, charts, items needing attention
 * and recent activity.
 */
import type { Summary } from "@shared/finance/ledger.ts";
import type { ReactNode } from "react";
import { Col, ListGroup, Row } from "react-bootstrap";
import { Link } from "react-router";
import { PropertyProfitChart } from "../components/charts/Charts.tsx";
import { ChartPanel, SummaryCards } from "../components/Financials.tsx";
import { Timeline } from "../components/Timeline.tsx";
import { DateRangePicker } from "../components/ui/DateRangePicker.tsx";
import {
  Can,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatCard,
  StatusBadge,
} from "../components/ui/kit.tsx";
import { useAuth } from "../lib/auth.tsx";
import { useFormat } from "../lib/format.ts";
import { useApi } from "../lib/query.ts";
import { useDateRange } from "../lib/url-state.ts";

interface DashboardData {
  financials: Summary | null;
  counts: { properties: number; occupied: number; vacant: number; outstandingRentPence: number };
  attention: {
    overdueTasks: Array<{
      id: string;
      title: string;
      dueDate: string;
      priority: string;
      addressLine1: string | null;
    }>;
    upcomingTasks: Array<{
      id: string;
      title: string;
      dueDate: string;
      priority: string;
      addressLine1: string | null;
    }>;
    compliance: Array<{
      id: string;
      typeName: string;
      expiryDate: string;
      addressLine1: string;
      status: string;
    }>;
    rentIssues: Array<{
      tenancyId: string;
      propertyLabel: string;
      outstandingPence: number;
      oldestDueDate: string;
    }>;
    maintenance: Array<{ id: string; title: string; status: string; priority: string; addressLine1: string }>;
    tenanciesEnding: Array<{ id: string; endingDate: string; confirmed: boolean; addressLine1: string }>;
    loansEnding: Array<{ id: string; lender: string; productEndDate: string; addressLine1: string }>;
  };
}

function AttentionList<T>({
  title,
  items,
  render,
  empty,
  to,
}: {
  title: string;
  items: T[];
  render: (item: T) => ReactNode;
  empty: string;
  to?: string;
}) {
  return (
    <Section
      title={`${title}${items.length ? ` (${items.length})` : ""}`}
      flush
      actions={
        to && (
          <Link to={to} className="small">
            View all
          </Link>
        )
      }
    >
      {items.length === 0 ? (
        <p className="small text-muted p-3 mb-0">{empty}</p>
      ) : (
        <ListGroup variant="flush">{items.map(render)}</ListGroup>
      )}
    </Section>
  );
}

export function DashboardPage() {
  const { me, can } = useAuth();
  const f = useFormat();
  const range = useDateRange();
  const scope = { from: range.from, to: range.to };
  const q = useApi<DashboardData>("/api/dashboard", scope, { keepPrevious: true });
  const pnl = useApi<{ properties: Array<{ key: string; label: string; summary: Summary }> }>(
    can("reports:read") ? "/api/reports/pnl" : null,
    scope,
    { keepPrevious: true },
  );
  return (
    <>
      <PageHeader
        title={`Welcome back, ${me?.user.name.split(" ")[0] ?? ""}`}
        subtitle="Your portfolio at a glance"
        actions={<DateRangePicker range={range} />}
      />
      <QueryState query={q}>
        {(d) =>
          d.counts.properties === 0 ? (
            <EmptyState
              icon="home"
              title="Let's add your first property"
              action={
                <Can permission="properties:write">
                  <IconButtonLink to="/properties/new" icon="plus">
                    Add property
                  </IconButtonLink>
                </Can>
              }
            >
              Once your properties are set up you can add tenancies and rent, record income and expenses,
              track compliance and produce tax-year reports.
            </EmptyState>
          ) : (
            <>
              {d.financials && (
                <SummaryCards
                  summary={d.financials}
                  scope={scope}
                  extra={
                    <Col sm={6} xl={3}>
                      <StatCard
                        label="Outstanding rent"
                        value={<Money pence={d.counts.outstandingRentPence} kind="outstanding" />}
                        tone="warning"
                        to="/reports/rent"
                        hint="As of today"
                      />
                    </Col>
                  }
                />
              )}
              <Row className="g-3 mb-4">
                <Col xs={4}>
                  <StatCard label="Properties" value={d.counts.properties} to="/properties" icon="home" />
                </Col>
                <Col xs={4}>
                  <StatCard label="Occupied" value={d.counts.occupied} to="/properties?occupancy=occupied" />
                </Col>
                <Col xs={4}>
                  <StatCard label="Vacant" value={d.counts.vacant} to="/properties?occupancy=vacant" />
                </Col>
              </Row>
              <Row>
                <Col xl={8}>
                  {can("reports:read") && <ChartPanel scope={scope} />}
                  {pnl.data && pnl.data.properties.length > 0 && (
                    <Section
                      title="Profitability by property"
                      actions={
                        <Link
                          to={`/reports/performance?from=${range.from}&to=${range.to}&preset=custom`}
                          className="small"
                        >
                          Compare properties
                        </Link>
                      }
                    >
                      <PropertyProfitChart groups={pnl.data.properties} />
                    </Section>
                  )}
                  <Row>
                    <Col md={6}>
                      <AttentionList
                        title="Overdue tasks"
                        to="/tasks?view=overdue"
                        items={d.attention.overdueTasks}
                        empty="Nothing overdue."
                        render={(t) => (
                          <ListGroup.Item key={t.id} action as={Link} to={`/tasks/${t.id}`}>
                            <div className="d-flex justify-content-between">
                              <span>{t.title}</span>
                              <span className="small text-danger">{f.date(t.dueDate)}</span>
                            </div>
                            {t.addressLine1 && <div className="small text-muted">{t.addressLine1}</div>}
                          </ListGroup.Item>
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <AttentionList
                        title="Upcoming tasks (14 days)"
                        to="/tasks?view=upcoming"
                        items={d.attention.upcomingTasks}
                        empty="No tasks due soon."
                        render={(t) => (
                          <ListGroup.Item key={t.id} action as={Link} to={`/tasks/${t.id}`}>
                            <div className="d-flex justify-content-between">
                              <span>{t.title}</span>
                              <span className="small text-muted">{f.date(t.dueDate)}</span>
                            </div>
                            {t.addressLine1 && <div className="small text-muted">{t.addressLine1}</div>}
                          </ListGroup.Item>
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <AttentionList
                        title="Compliance"
                        to="/compliance"
                        items={d.attention.compliance}
                        empty="Everything is in date."
                        render={(c) => (
                          <ListGroup.Item key={c.id} action as={Link} to={`/compliance/${c.id}`}>
                            <div className="d-flex justify-content-between gap-2">
                              <span>{c.typeName}</span>
                              <StatusBadge status={c.status} />
                            </div>
                            <div className="small text-muted">
                              {c.addressLine1} · expires {f.date(c.expiryDate)}
                            </div>
                          </ListGroup.Item>
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <AttentionList
                        title="Rent issues"
                        to="/reports/rent"
                        items={d.attention.rentIssues}
                        empty="No rent arrears."
                        render={(r) => (
                          <ListGroup.Item key={r.tenancyId} action as={Link} to={`/tenancies/${r.tenancyId}`}>
                            <div className="d-flex justify-content-between">
                              <span>{r.propertyLabel}</span>
                              <Money pence={r.outstandingPence} kind="outstanding" />
                            </div>
                            <div className="small text-muted">Unpaid since {f.date(r.oldestDueDate)}</div>
                          </ListGroup.Item>
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <AttentionList
                        title="Outstanding maintenance"
                        to="/maintenance"
                        items={d.attention.maintenance}
                        empty="No open maintenance."
                        render={(m) => (
                          <ListGroup.Item key={m.id} action as={Link} to={`/maintenance/${m.id}`}>
                            <div className="d-flex justify-content-between gap-2">
                              <span>{m.title}</span>
                              <StatusBadge status={m.priority} />
                            </div>
                            <div className="small text-muted">
                              {m.addressLine1} · {m.status.replace("_", " ")}
                            </div>
                          </ListGroup.Item>
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <AttentionList
                        title="Other reminders"
                        items={[
                          ...d.attention.tenanciesEnding.map((t) => ({ kind: "tenancy" as const, ...t })),
                          ...d.attention.loansEnding.map((l) => ({ kind: "loan" as const, ...l })),
                        ]}
                        empty="No tenancies or mortgage deals ending soon."
                        render={(x) =>
                          x.kind === "tenancy" ? (
                            <ListGroup.Item key={x.id} action as={Link} to={`/tenancies/${x.id}`}>
                              Tenancy {x.confirmed ? "ends" : "fixed term ends"} {f.date(x.endingDate)}
                              <div className="small text-muted">{x.addressLine1}</div>
                            </ListGroup.Item>
                          ) : (
                            <ListGroup.Item key={x.id} action as={Link} to={`/finance/${x.id}`}>
                              {x.lender} deal ends {f.date(x.productEndDate)}
                              <div className="small text-muted">{x.addressLine1}</div>
                            </ListGroup.Item>
                          )
                        }
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xl={4}>
                  <Section
                    title="Recent activity"
                    actions={
                      <Link to="/timeline" className="small">
                        View all
                      </Link>
                    }
                  >
                    <Timeline filters={{}} limit={12} showFilters={false} />
                  </Section>
                </Col>
              </Row>
            </>
          )
        }
      </QueryState>
    </>
  );
}
